import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react";
import { fetchJson } from "@mobility-item-page/client";
import { Intent } from "@mobility-item-page/utilities/client";
import { toast } from "@warp-ds/elements/toast";
import { Button } from "@warp-ds/react";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { useValidDealerContext } from "../context/DealerProviderContext.tsx";
import { useDealerUIState } from "../context/DealerUIStateContext.tsx";
import { useTracking } from "../utils/pulseHelper.ts";
import { visibleActionsHelper } from "../utils/visibleActionsHelper.ts";
import { openContactFormEventId } from "./modals/ContactFormModal.tsx";
import { eidToSendMessageEventId } from "./modals/EidModal.tsx";
import { logInToSendMessageEventId } from "./modals/LoginModal.tsx";
import { ReserveButton, ReserveCar } from "./ReserveCar.tsx";

type Props = {
  position: ObjectPosition;
};

export const VisibleActions = ({ position }: Props) => {
  const {
    itemData: { adId, hideWriteToSeller, salesPersonContactInfo, active },
    organisationData: { phoneNumber: orgPhoneNumber },
    external: { isMobile, isLoggedIn, reservationEnabled, isItemReserved, isPreview },
    resolved: { phoneNumberCallbackUrl },
  } = useValidDealerContext();

  if (!active && !isPreview) {
    return null;
  }

  const components: ReactNode[] = [];

  const addComponent = (component: ReactNode) => {
    if (component) components.push(component);
  };

  const resolvedPhoneNumber = salesPersonContactInfo?.phone?.phoneNumberFallback ?? orgPhoneNumber;

  visibleActionsHelper({
    position,
    hideWriteToSeller,
    phoneNumberCallbackUrl,
    orgPhoneNumber: resolvedPhoneNumber,
    reservationEnabled,
    isItemReserved,
  }).forEach((component) => {
    switch (component) {
      case "ReserveCar":
        addComponent(<ReserveCar key="reserve-car" />);
        break;
      case "ReserveButton":
        addComponent(<ReserveButton key="reserve-button" />);
        break;
      case "Contact":
        addComponent(
          <Contact
            key="contact"
            adId={adId}
            isLoggedIn={isLoggedIn}
            primary={
              (position === ObjectPosition.NonStickyCTA &&
                reservationEnabled &&
                isItemReserved &&
                components.length === 1) ||
              components.length === 0
            }
            position={position}
            isPreview={isPreview}
          />,
        );
        break;
      case "Phonenumber":
        addComponent(
          <Phonenumber
            key="phone"
            isMobile={isMobile}
            orgPhoneNumber={resolvedPhoneNumber}
            primary={
              (position === ObjectPosition.NonStickyCTA &&
                reservationEnabled &&
                isItemReserved &&
                components.length === 1) ||
              components.length === 0
            }
            position={position}
            phoneNumberCallbackUrl={phoneNumberCallbackUrl}
            isPreview={isPreview}
            directCallText={salesPersonContactInfo?.phone?.phoneNumberDirectCallText}
          />,
        );
        break;
    }
  });

  return <>{components}</>;
};

const Contact = ({
  adId,
  position = ObjectPosition.NonStickyCTA,
  isLoggedIn,
  primary = false,
  isPreview = false,
}: {
  adId: number;
  position?: ObjectPosition;
  isLoggedIn: boolean;
  primary?: boolean;
  isPreview?: boolean;
}) => {
  const { trackContactUs, trackOpenLoginModal } = useTracking();
  const {
    itemData: { salesPersonContactInfo },
    external: { newContactForm, eidAuth, openContactForm },
  } = useValidDealerContext();

  const openNextModal = useCallback(() => {
    if (!isLoggedIn) {
      const event = new CustomEvent(logInToSendMessageEventId);
      window.dispatchEvent(event);
      trackOpenLoginModal();
      return;
    }

    if (newContactForm && eidAuth.userMustVerify) {
      const event = new CustomEvent(eidToSendMessageEventId);
      window.dispatchEvent(event);
      return;
    }

    if (newContactForm) {
      const event = new CustomEvent(openContactFormEventId);
      window.dispatchEvent(event);
    }
  }, [isLoggedIn, newContactForm, eidAuth, trackOpenLoginModal]);

  const onClick = () => {
    trackContactUs(position);
    openNextModal();
  };

  useEffect(() => {
    if (!isPreview && openContactForm) {
      const url = new URL(window.location.href);
      url.searchParams.delete("contact");
      window.history.replaceState({}, document.title, url.toString());
      openNextModal();
    }
  }, [openContactForm, isPreview, openNextModal]);

  const resolvedHref = isLoggedIn && !newContactForm ? `/contact/${adId}` : undefined;
  const resolvedText =
    salesPersonContactInfo?.email.sendEmailLinkText && position === ObjectPosition.InfoCard
      ? salesPersonContactInfo.email.sendEmailLinkText
      : i18n.t({ id: "dealer.actions.contact", message: "Write to seller" });
  const fullWidth = position !== ObjectPosition.InfoCard;

  if (isPreview) {
    if (position === ObjectPosition.InfoCard && resolvedHref) {
      return (
        <p className="whitespace-nowrap underline s-text-link" data-testid="contact">
          {resolvedText}
        </p>
      );
    }

    return (
      <Button
        link={position === ObjectPosition.InfoCard}
        data-testid="contact"
        primary={primary && position !== ObjectPosition.InfoCard}
        fullWidth={fullWidth}
        className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
      >
        {resolvedText}
      </Button>
    );
  }

  if (position === ObjectPosition.InfoCard && resolvedHref) {
    return (
      <a href={resolvedHref} className="whitespace-nowrap" data-testid="contact" onClick={onClick}>
        {resolvedText}
      </a>
    );
  }

  return (
    <Button
      link={position === ObjectPosition.InfoCard}
      data-testid="contact"
      primary={primary && position !== ObjectPosition.InfoCard}
      fullWidth={fullWidth}
      className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
      href={resolvedHref}
      onClick={onClick}
    >
      {resolvedText}
    </Button>
  );
};

const Phonenumber = ({
  primary,
  isMobile,
  orgPhoneNumber,
  position = ObjectPosition.NonStickyCTA,
  phoneNumberCallbackUrl,
  isPreview = false,
  directCallText,
}: {
  primary?: boolean;
  isMobile?: boolean;
  orgPhoneNumber?: string;
  position?: ObjectPosition;
  phoneNumberCallbackUrl?: string;
  isPreview?: boolean;
  directCallText?: string;
}) => {
  const { trackPhone, trackShowPhone } = useTracking();
  const {
    hiddenPhone,
    setHiddenPhone,
    phoneNumber,
    setPhoneNumber,
    isLoadingTrackedPhoneNumber,
    setIsLoadingTrackedPhoneNumber,
    hasTrackedPhoneNumberFailed,
    setHasTrackedPhoneNumberFailed,
  } = useDealerUIState();
  const { i18n } = useLingui();
  const errorState = useMemo(
    () => hasTrackedPhoneNumberFailed && !phoneNumber,
    [hasTrackedPhoneNumberFailed, phoneNumber],
  );

  const text = useMemo(() => {
    const showPhoneNumberText = i18n.t({ id: "dealer.actions.phonenumber.show", message: "Show phone number" });
    if (hiddenPhone) {
      return showPhoneNumberText;
    }
    if (phoneNumber) {
      return phoneNumber;
    }
    // If there's no number to show, an error text will be shown
    return showPhoneNumberText;
  }, [hiddenPhone, i18n, phoneNumber]);

  const fetchTrackedPhoneNumber = useCallback(async () => {
    try {
      setIsLoadingTrackedPhoneNumber(true);
      setHasTrackedPhoneNumberFailed(false);

      let phoneNumber: string | undefined;

      if (phoneNumberCallbackUrl) {
        // NOTE: The dev server must run on port 8080 (default port) to avoid CORS issues locally
        const response = await fetchJson<{ phoneNumber?: string }>(phoneNumberCallbackUrl);
        if (response.ok && response.data?.phoneNumber) {
          phoneNumber = response.data.phoneNumber;
          setPhoneNumber(phoneNumber);
          return phoneNumber;
        }
      }

      phoneNumber = orgPhoneNumber;
      setPhoneNumber(phoneNumber);
      setHasTrackedPhoneNumberFailed(true);
      return phoneNumber;
    } catch (error) {
      const phoneNumber = orgPhoneNumber;
      setPhoneNumber(phoneNumber);
      setHasTrackedPhoneNumberFailed(true);
      console.error("Failed to fetch tracked phone number:", error);
      return phoneNumber;
    } finally {
      setIsLoadingTrackedPhoneNumber(false);
    }
  }, [
    phoneNumberCallbackUrl,
    setHasTrackedPhoneNumberFailed,
    setIsLoadingTrackedPhoneNumber,
    setPhoneNumber,
    orgPhoneNumber,
  ]);

  if (!orgPhoneNumber && !phoneNumberCallbackUrl) {
    return null;
  }
  const fullWidth = position !== ObjectPosition.InfoCard;

  if (isPreview) {
    return (
      <div className="w-full">
        <Button
          data-testid="phone-number"
          primary={primary && position !== ObjectPosition.InfoCard}
          link={position === ObjectPosition.InfoCard}
          fullWidth={fullWidth}
          className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
        >
          <Trans id="dealer.actions.phonenumber.show" message="Show phone number" />
        </Button>
      </div>
    );
  }

  const errorDescription = i18n.t({
    id: "dealer.actions.phonenumber.error.description",
    message: "Something went wrong.",
    comment: "Visible if something went wrong when trying to fetch the number",
  });

  const onDesktopClick = async () => {
    if (hiddenPhone) {
      trackShowPhone(position);
      const phoneNumber = await fetchTrackedPhoneNumber();
      if (phoneNumber) setHiddenPhone(false);
    } else {
      trackPhone(position, Intent.Click);
      copyToClipboard(phoneNumber);
      toast(
        i18n.t({
          id: "dealer.actions.phonenumber.copied",
          message: "Copied",
        }),
      );
    }
  };

  const onMobileClick = async () => {
    let numberToCall = phoneNumber;

    if (!numberToCall) {
      numberToCall = await fetchTrackedPhoneNumber();
    }
    trackPhone(position, Intent.Call);

    // If there's no number to call, an error text will be shown
    if (numberToCall) {
      window.location.href = `tel:${numberToCall}`;
    }
  };

  if (isMobile) {
    return (
      <>
        {position === ObjectPosition.InfoCard && isLoadingTrackedPhoneNumber ? (
          <div className="animate-spinner [--spinner-size:16px] my-4"></div>
        ) : (
          <div className={fullWidth ? "w-full" : ""}>
            <Button
              link={position === ObjectPosition.InfoCard}
              primary={primary && position !== ObjectPosition.InfoCard}
              className={`whitespace-nowrap ${fullWidth ? "md:min-w-[170px]" : ""}`}
              fullWidth={fullWidth}
              onClick={onMobileClick}
              loading={isLoadingTrackedPhoneNumber}
            >
              {position === ObjectPosition.InfoCard && directCallText ? (
                directCallText
              ) : (
                <Trans id="dealer.actions.phonenumber.call" message="Call seller" />
              )}
            </Button>
            {errorState ? <p className="s-text-negative pt-8"> {errorDescription} </p> : null}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {position === ObjectPosition.InfoCard && isLoadingTrackedPhoneNumber ? (
        <div className="animate-spinner [--spinner-size:16px] my-4"></div>
      ) : (
        <div className={fullWidth ? "w-full" : ""}>
          <Button
            link={position === ObjectPosition.InfoCard}
            data-testid="phone-number"
            primary={primary && position !== ObjectPosition.InfoCard}
            fullWidth={fullWidth}
            className={`whitespace-nowrap flex items-center ${fullWidth ? "md:min-w-[170px]" : ""}`}
            onClick={onDesktopClick}
            loading={isLoadingTrackedPhoneNumber}
          >
            {text}
          </Button>
          {errorState ? <p className="s-text-negative pt-8"> {errorDescription} </p> : null}
        </div>
      )}
    </>
  );
};

const copyToClipboard = async (textToCopy: string | undefined) => {
  if (!textToCopy) {
    return;
  }
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;

    textArea.style.position = "absolute";
    textArea.style.left = "-999999px";

    document.body.prepend(textArea);
    textArea.select();

    try {
      document.execCommand("copy");
    } catch (error) {
      console.error(error);
    } finally {
      textArea.remove();
    }
  }
};
